import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Smartphone, EyeOff, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN = 'Daquan22';
const money = (v) => `$${Number(v).toFixed(2)}`;
const pretty = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const DayCard = ({ day, onDone }) => {
  const [picked, setPicked] = useState([]);
  const [restCash, setRestCash] = useState(false);
  const [busy, setBusy] = useState(false);
  const sum = day.payments.filter((p) => picked.includes(p.id)).reduce((s, p) => s + p.amount, 0);
  const toggle = (id) => setPicked((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));

  const assign = async (s) => {
    setBusy(true);
    try {
      await axios.post(`${API}/cashapp/assign`, { admin_secret: ADMIN, booking_id: s.id, payment_ids: picked, rest_cash: restCash });
      toast.success(`${s.name} marked paid`);
      setPicked([]);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record');
    } finally {
      setBusy(false);
    }
  };
  const ignore = async (p) => {
    try {
      await axios.post(`${API}/cashapp/ignore`, { admin_secret: ADMIN, payment_id: p.id, undo: p.status === 'ignored' });
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div data-testid={`cashapp-day-${day.date}`} className="rounded-xl border border-neutral-800 bg-neutral-950 p-4">
      <p className="text-white font-semibold mb-3">{pretty(day.date)}</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">1. Tap the Cash App payments</p>
          <div className="space-y-1.5">
            {day.payments.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <button
                  data-testid={`cashapp-pay-${p.id}`}
                  disabled={p.status === 'ignored'}
                  onClick={() => toggle(p.id)}
                  className={`flex-1 flex justify-between px-3 py-2 rounded-lg border text-sm transition-colors duration-150 disabled:opacity-40 ${picked.includes(p.id) ? 'border-purple-500 bg-purple-500/15 text-white' : 'border-neutral-800 text-neutral-300 hover:border-neutral-600'}`}
                >
                  <span className="truncate">{p.name}</span><b>{money(p.amount)}</b>
                </button>
                <button title={p.status === 'ignored' ? 'Restore' : 'Not a session balance'} data-testid={`cashapp-ignore-${p.id}`} onClick={() => ignore(p)} className="p-2 text-neutral-500 hover:text-neutral-200">
                  <EyeOff size={14} />
                </button>
              </div>
            ))}
          </div>
          {picked.length > 0 && <p className="text-sm text-purple-300 mt-2" data-testid={`cashapp-picked-sum-${day.date}`}>Selected: {money(sum)}</p>}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">2. Tap the session they paid for</p>
          {day.sessions.length === 0 ? <p className="text-neutral-500 text-sm">No open sessions around this day.</p> : (
            <div className="space-y-1.5">
              {day.sessions.map((s) => (
                <button
                  key={s.id}
                  data-testid={`cashapp-session-${s.booking_number}`}
                  disabled={!picked.length || busy}
                  onClick={() => assign(s)}
                  className="w-full flex justify-between items-center px-3 py-2 rounded-lg border border-neutral-800 text-sm text-neutral-200 hover:border-purple-500 disabled:opacity-40 disabled:hover:border-neutral-800 transition-colors duration-150"
                >
                  <span className="text-left truncate">{s.name} <span className="text-neutral-500 text-xs">{s.date !== day.date ? pretty(s.date) : ''} {s.time} · {s.engineer_name}</span></span>
                  <b className="text-purple-300">{money(s.balance)}</b>
                </button>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 mt-3 text-xs text-neutral-400">
            <input type="checkbox" checked={restCash} onChange={(e) => setRestCash(e.target.checked)} data-testid={`cashapp-rest-cash-${day.date}`} />
            Rest of the balance was paid in cash
          </label>
        </div>
      </div>
    </div>
  );
};

const StatementUpload = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const fd = new FormData();
    fd.append('admin_secret', ADMIN);
    fd.append('file', f);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/cashapp/upload`, fd);
      toast.success(`${data.found} payments found · ${data.new} new · ${data.already_loaded} already loaded`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };
  return (
    <label data-testid="cashapp-upload-btn" className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-purple-500/50 text-purple-300 cursor-pointer transition-colors hover:bg-purple-500/10 ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
      <Upload size={14} /> {busy ? 'Reading…' : 'Upload statement'}
      <input type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid="cashapp-upload-input" />
    </label>
  );
};

export const CashAppReview = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/cashapp/review`, { params: { admin_secret: ADMIN, show_ignored: showIgnored } })
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load Cash App review'));
  }, [showIgnored]);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChange?.(); };

  return (
    <div data-testid="cashapp-review" className="bg-black/40 border border-neutral-800 rounded-2xl p-5 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Smartphone size={20} className="text-purple-400" />
          <h3 className="text-white font-bold text-lg">Cash App review</h3>
        </div>
        <div className="flex items-center gap-3">
          {data && <p className="text-sm text-neutral-400" data-testid="cashapp-review-summary">{data.unassigned} unassigned · <b className="text-white">{money(data.total)}</b></p>}
          <StatementUpload onDone={load} />
        </div>
      </div>
      <p className="text-neutral-500 text-xs mb-4">From your uploaded statements. Pick the payment(s), then the session. It marks the balance paid and records it in Wave. Hide payments that weren't for a balance.</p>
      <label className="flex items-center gap-2 mb-4 text-xs text-neutral-400">
        <input type="checkbox" checked={showIgnored} onChange={(e) => setShowIgnored(e.target.checked)} data-testid="cashapp-show-ignored" /> Show hidden payments
      </label>
      {!data ? null : data.days.length === 0 ? <p className="text-neutral-400 text-sm">All Cash App payments are sorted.</p> : (
        <div className="space-y-3 max-h-[720px] overflow-y-auto pr-1">
          {data.days.map((d) => <DayCard key={d.date} day={d} onDone={done} />)}
        </div>
      )}
    </div>
  );
};
